import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Menu, X } from 'lucide-react';
import { NAV } from '@/constants/testIds';

const LINKS = [
  { id: 'services', label: 'Services', testId: NAV.linkServices },
  { id: 'work',     label: 'Work',     testId: NAV.linkWork     },
  { id: 'process',  label: 'Process',  testId: NAV.linkProcess  },
  { id: 'pricing',  label: 'Pricing',  testId: NAV.linkPricing  },
  { id: 'contact',  label: 'Contact',  testId: NAV.linkContact  },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while the mobile menu is open. Plain `overflow: hidden`
  // doesn't reliably stop touch/momentum scrolling on iOS Safari, which lets
  // the page scroll behind the panel and can break the backdrop-blur
  // rendering. Pinning the body with `position: fixed` and restoring scroll
  // position on close is the robust fix.
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const { style } = document.body;
    const original = { position: style.position, top: style.top, width: style.width };

    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.width = '100%';

    return () => {
      style.position = original.position;
      style.top = original.top;
      style.width = original.width;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${(scrolled || open) ? 'glass-nav' : 'bg-transparent'}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-[68px] flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center"
          data-testid={NAV.logo}
          aria-label="SiteHive home"
        >
          <img src="/logo.png" alt="SiteHive" className="h-9 w-auto" />
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => scrollToId(l.id)}
              className="nav-link-underline text-[13.5px] font-medium text-[color:var(--sh-ink)]/85 hover:text-[color:var(--sh-ink)] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <span
            data-testid={NAV.liveBadge}
            className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-emerald-600/30 bg-emerald-50 text-emerald-800 text-[11px] font-medium"
            title="Live now"
          >
            <span className="live-dot" />
            <span className="font-accent tracking-wide">3 builds in progress</span>
          </span>

          <button
            data-testid={NAV.ctaStart}
            onClick={() => scrollToId('contact')}
            className="btn-amber hidden md:inline-flex items-center gap-2"
          >
            Start a Project
            <ArrowRight size={14} />
          </button>

          <button
            data-testid={NAV.mobileToggle}
            className="lg:hidden p-2 rounded-md border border-[color:var(--sh-line)] text-[color:var(--sh-ink)]"
            onClick={() => setOpen((s) => !s)}
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobileOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{ top: 68 }}
            className="fixed inset-x-0 bottom-0 z-40 lg:hidden bg-[color:var(--sh-bg)]/97 backdrop-blur-md"
          >
            <div className="px-6 pt-10 pb-8 h-full flex flex-col">
              <div className="flex-1 flex flex-col">
                {LINKS.map((l, i) => (
                  <motion.button
                    key={l.id}
                    data-testid={`${l.testId}-mobile`}
                    onClick={() => { setOpen(false); setTimeout(() => scrollToId(l.id), 250); }}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.04 + i * 0.05, duration: 0.3 }}
                    className="text-left group flex items-center justify-between border-b border-[color:var(--sh-line)] py-5"
                  >
                    <span className="flex items-center gap-4">
                      <span className="font-accent text-[color:var(--sh-amber-2)] text-xs tracking-widest">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="font-display text-2xl text-[color:var(--sh-ink)] group-hover:text-[color:var(--sh-amber-2)] transition-colors">
                        {l.label}
                      </span>
                    </span>
                    <ArrowRight size={20} className="text-[color:var(--sh-muted)] group-hover:text-[color:var(--sh-amber-2)] transition-colors" />
                  </motion.button>
                ))}
              </div>
              <motion.button
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.3 }}
                onClick={() => { setOpen(false); setTimeout(() => scrollToId('contact'), 250); }}
                className="btn-amber w-full flex items-center justify-center gap-2 mt-2"
              >
                Start a Project <ArrowRight size={16} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
